// Impact case-study data — each case has a sections array rendered by ImpactCase.jsx.
// Sections may include: {type:"hero",...}, {type:"chapter",n,t,h,body,image}, {type:"stats",
// items}, {type:"flow",steps}, {type:"quote",text}, {type:"capabilities",items}, etc.

export const IMPACT_CASES = {
  "ditch-the-screens": {
    slug: "ditch-the-screens",
    client: "Scouts Canada",
    project: "Ditch the Screens",
    category: "National Youth Recruitment",
    year: "2024",
    cover: "https://images.pexels.com/photos/8613089/pexels-photo-8613089.jpeg?w=2400&q=85",
    heroTitle: ["DITCH", "THE", "SCREENS."],
    heroKicker: "SCOUTS CANADA · NATIONAL YOUTH RECRUITMENT",
    heroStats: [
      { big: "27,000", label: "MEMBERS ACQUIRED" },
      { big: "≈ $7.56M", label: "ESTIMATED MEMBERSHIP VALUE" },
    ],
    heroNote: "Calculated using a $280 membership fee.",
    sections: [
      { type: "chapter", n: "01", t: "Listen", h: ["Start with", "parents."],
        body: "National parent survey research uncovered what families were thinking, the challenges they were experiencing and what they wanted more of for their children. Those insights became the foundation of the campaign strategy." },
      { type: "chapter", n: "02", t: "The Idea", h: ["Ditch", "the screens."],
        body: "Research transformed into a national creative platform positioning Scouting as an alternative to increasingly screen-dominated childhood. Exploration, friendship, adventure, skill and real-world experience." },
      { type: "chapter", n: "03", t: "Production", h: ["From strategy", "to camp."],
        body: "Developed the campaign concept, built the strategy, wrote the video scripts, worked with the production team, cast youth talent, helped direct creative execution. Production took place over one weekend at Camp Samac.",
        gallery: [
          "https://images.pexels.com/photos/9127599/pexels-photo-9127599.jpeg?w=1200",
          "https://images.pexels.com/photos/9127602/pexels-photo-9127602.jpeg?w=1200",
          "https://images.pexels.com/photos/6667650/pexels-photo-6667650.jpeg?w=1200",
        ]},
      { type: "chapter", n: "04", t: "The Journey", h: ["An ad is only", "the beginning."],
        body: "The campaign included a dedicated landing-page experience and a three-month national email journey. The CRM strategy included prospective and previous members, including audiences dating back approximately two years.",
        flow: ["Parent Research", "Campaign Strategy", "Creative + Video", "Digital Advertising", "Landing Page", "Email Nurture", "Registration"] },
      { type: "result", h: ["27,000 members.", "≈ $7.56M value."],
        stats: [
          { big: "27,000", label: "MEMBERS ACQUIRED THROUGH DIGITAL ADVERTISING" },
          { big: "≈ $7.56M", label: "ESTIMATED MEMBERSHIP VALUE" },
        ],
        note: "Based on a $280 membership fee. This is an estimated membership value, not audited campaign revenue." },
    ],
    capabilities: ["Research", "Strategy", "Creative", "Scripting", "Production", "Paid Media", "Landing Page", "CRM"],
    next: "scout-popcorn",
  },

  "scout-popcorn": {
    slug: "scout-popcorn",
    client: "Scouts Canada",
    project: "Scout Popcorn",
    category: "National Fundraising Turnaround",
    year: "2023",
    cover: "https://images.pexels.com/photos/5533229/pexels-photo-5533229.jpeg?w=2400&q=85",
    heroTitle: ["SCOUT", "POPCORN."],
    heroKicker: "SCOUTS CANADA · NATIONAL FUNDRAISING",
    heroTagline: ["From an at-risk", "program to $700K."],
    heroStats: [
      { big: "$200K → $700K", label: "ANNUAL REVENUE" },
      { big: "250%", label: "REVENUE GROWTH" },
      { big: "170 → 430", label: "PARTICIPATING SCOUT GROUPS" },
      { big: "153%", label: "PARTICIPATION GROWTH" },
    ],
    sections: [
      { type: "chapter", n: "01", t: "The Challenge", h: ["A national fundraiser", "at a crossroads."],
        body: "Scout Popcorn generated approximately $200K in revenue with only 170 participating Scout Groups the previous year and was at risk of being discontinued. The challenge was not simply to sell more popcorn — it was to rebuild participation and give families, Groups and supporters stronger reasons to participate." },
      { type: "chapter", n: "02", t: "The Strategy", h: ["Rebuild", "the journey."],
        body: "A new national marketing and engagement strategy combined audience segmentation, targeted email drip campaigns, lifecycle communications, incentive strategy, prize-tier restructuring and national campaign execution.",
        flow: ["Parents", "Scout Groups", "Supporters", "Targeted Journeys", "Participation", "Purchase"] },
      { type: "chapter", n: "03", t: "Email + CRM", h: ["The right message.", "The right moment."],
        body: "Targeted email journeys were developed for parents, Scout Groups and supporters. Communications were segmented and timed throughout the fundraising campaign.",
        flow: ["Awareness", "Interest", "Participation", "Purchase", "Continued Engagement"] },
      { type: "chapter", n: "04", t: "Incentives", h: ["Give people something", "worth working toward."],
        body: "The incentive and prize strategy was reworked around targeted prize tiers and rewards participants actually cared about." },
      { type: "result", h: ["From survival", "to growth."],
        stats: [
          { big: "$200K → $700K", label: "PREVIOUS → ACHIEVED" },
          { big: "250%", label: "REVENUE GROWTH" },
          { big: "170 → 430", label: "GROUPS BEFORE → AFTER" },
          { big: "153%", label: "PARTICIPATION GROWTH" },
        ]},
      { type: "result", h: ["Beating", "the target."],
        stats: [
          { big: "$400K → $700K", label: "TARGET → ACHIEVED" },
          { big: "+ $300K", label: "ABOVE TARGET" },
          { big: "240 → 430", label: "GROUPS TARGET → ACHIEVED" },
          { big: "+ 190", label: "ABOVE TARGET" },
        ]},
    ],
    capabilities: ["Fundraising Strategy", "Campaign Strategy", "Email Marketing", "CRM", "Segmentation", "Lifecycle Marketing", "Incentive Strategy", "Revenue Growth"],
    next: "scouts-volunteer-recruitment",
  },

  "scouts-volunteer-recruitment": {
    slug: "scouts-volunteer-recruitment",
    client: "Scouts Canada",
    project: "National Volunteer Recruitment",
    category: "Lead Generation & Onboarding",
    year: "2023",
    cover: "https://images.pexels.com/photos/8613319/pexels-photo-8613319.jpeg?w=2400&q=85",
    heroTitle: ["FROM INTEREST", "TO ACTION."],
    heroKicker: "SCOUTS CANADA · NATIONAL VOLUNTEER RECRUITMENT",
    heroStats: [{ big: "2,000+", label: "VOLUNTEER LEADS" }],
    sections: [
      { type: "chapter", n: "01", t: "The Challenge", h: ["Attract nationally.", "Convert locally."],
        body: "Scouts Canada needed to attract prospective volunteers nationally while helping local Scout Groups turn interest into meaningful conversations and onboarding." },
      { type: "chapter", n: "02", t: "Acquisition", h: ["2,000+ leads,", "nationally."],
        body: "Led the development of national volunteer recruitment marketing designed to generate prospective volunteer leads.",
        stats: [{ big: "2,000+", label: "VOLUNTEER LEADS" }]},
      { type: "chapter", n: "03", t: "Conversion", h: ["The lead was", "only the beginning."],
        body: "The strategy extended beyond advertising. A structured follow-up and onboarding system was developed to help Group Commissioners contact prospective volunteers — text outreach scripts, follow-up messaging, conversation guidance and an onboarding approach." },
      { type: "chapter", n: "04", t: "Enablement", h: ["Train the people", "who show up."],
        body: "Led training for Group Commissioners on how to use the scripts, contact prospective volunteers and move leads toward meaningful conversations and onboarding.",
        flow: ["Campaign", "Lead", "Local Group", "Text Outreach", "Conversation", "Onboarding"] },
      { type: "result", h: ["2,000+ prospective", "volunteer leads."],
        stats: [{ big: "2,000+", label: "PROSPECTIVE VOLUNTEER LEADS" }]},
    ],
    capabilities: ["Campaign Strategy", "Lead Generation", "Conversion", "Scripting", "Onboarding", "Training"],
    next: "chipmunks",
  },

  "chipmunks": {
    slug: "chipmunks",
    client: "Scouts Canada",
    project: "Chipmunks Early Years Pilot",
    category: "Program Development / Innovation",
    year: "2024",
    cover: "https://images.pexels.com/photos/8613056/pexels-photo-8613056.jpeg?w=2400&q=85",
    heroTitle: ["CHIPMUNKS."],
    heroKicker: "SCOUTS CANADA · EARLY YEARS",
    heroTagline: ["Building a new", "Scouting program", "from the ground up."],
    sections: [
      { type: "chapter", n: "01", t: "The Opportunity", h: ["What if the Scouting", "journey started earlier?"],
        body: "Chipmunks began as an internal exploration: developing and testing a new Early Years program offering within a national organization." },
      { type: "chapter", n: "02", t: "From Idea to Pilot", h: ["Concept.", "Test.", "Launch."],
        body: "Involvement in helping develop the initiative internally — developing the Chipmunks name, helping shape the concept, working cross-functionally with internal teams, helping build and refine the program proposition, internal testing, and supporting development toward pilot and launch.",
        flow: ["Opportunity", "Concept", "Positioning", "Naming", "Chipmunks", "Internal Testing", "Program Development", "Pilot"] },
      { type: "naming", h: "Beavers. Cubs. Scouts.", sub: "And now…", finale: "CHIPMUNKS." },
      { type: "chapter", n: "04", t: "Building Something New", h: ["Early concepts,", "long conversations."],
        body: "Program materials, naming work, internal development sessions, positioning workshops and pilot imagery — all part of the broader program and brand development process." },
    ],
    capabilities: ["Program Development", "Naming", "Positioning", "Internal Collaboration", "Testing", "Launch"],
    externalLink: { label: "View Scouts Canada · Chipmunks", url: "https://www.scouts.ca/programs/chipmunks/overview.html" },
    next: "raise-chess-giving-tuesday",
  },

  "raise-chess-giving-tuesday": {
    slug: "raise-chess-giving-tuesday",
    client: "Raise Chess Academy",
    project: "Giving Tuesday",
    category: "Digital Fundraising",
    year: "2024",
    cover: "https://images.pexels.com/photos/163064/play-stone-network-networked-interactive-163064.jpeg?w=2400&q=85",
    heroTitle: ["$100 CAD IN.", "$961 USD RAISED."],
    heroKicker: "RAISE CHESS ACADEMY · GIVING TUESDAY",
    sections: [
      { type: "chapter", n: "01", t: "The Challenge", h: ["Real money", "from a small budget."],
        body: "Create a focused Giving Tuesday fundraising campaign capable of generating donations despite an extremely limited paid-media budget." },
      { type: "chapter", n: "02", t: "The Campaign", h: ["One story.", "Seven days.", "One budget."],
        body: "Good Bandit developed and executed a one-week social media and digital advertising campaign across Meta platforms, including Facebook and Instagram. The campaign connected fundraising story, social content, paid distribution and a clear donation objective.",
        flow: ["Fundraising Story", "Social Content", "Paid Distribution", "Donation Objective"] },
      { type: "result", h: ["Small budget.", "Real impact."],
        stats: [
          { big: "$100 CAD", label: "META AD SPEND" },
          { big: "7 DAYS", label: "CAMPAIGN" },
          { big: "$961 USD", label: "RAISED" },
        ],
        note: "Because campaign spend and funds raised are denominated in different currencies, we do not display a conventional ROAS." },
    ],
    capabilities: ["Fundraising Strategy", "Campaign Development", "Social Media", "Meta Advertising", "Copy", "Digital Fundraising"],
    next: "ditch-the-screens",
  },
};

export const IMPACT_ORDER = [
  "ditch-the-screens",
  "scout-popcorn",
  "scouts-volunteer-recruitment",
  "chipmunks",
  "raise-chess-giving-tuesday",
];
